import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _get from "@babel/runtime/helpers/get";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _createSuper from "@babel/runtime/helpers/createSuper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["forwardRef"],
  _excluded2 = ["forwardRef", "__WRAPPER_PROPS_BIND__"],
  _excluded3 = ["ref"];
import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { useForkRef } from "../ref.mjs";
import useEnhancedEffect from "../use/useEnhancedEffect.mjs";
import _assignProps from "../assignProps.mjs";
import { Component } from "./types/Component.mjs";
import register from "./register.mjs";
import childrenEnhancement, { CHILDREN_COMPONENT } from "./enhancement/Children.mjs";
import rootEnhancement from "./enhancement/Root.mjs";
import uncontrolledPropsEnhancement from "./enhancement/uncontrolledProps.mjs";
import functionDefaultPropsEnhancement from "./enhancement/functionDefaultProps.mjs";
import staticChildrenEnhancement, { STATIC_COMPONENT, ROOT_COMPONENT, getterMethodNameByDisplayName } from "./enhancement/staticChildren.mjs";
import inheritedNameEnhancement, { INHERITED_NAME } from "./enhancement/inheritedName.mjs";
import hoistPropsEnhancement from "./enhancement/hoistProps.mjs";
import dataNameEnhancement from "./enhancement/dataName.mjs";
import enhanceEnhancement from "./enhancement/enhance.mjs";
import styleEnhancement from "./enhancement/style.mjs";
import bindHandlerEnhancement from "./enhancement/bindHandler.mjs";
import i18nAppLocaleEnhance from "./enhancement/i18n.mjs";
import { CORE_AS_PROPS, CORE_INIT, PARENT_COMPONENTS, CORE_INSTANCE, CORE_COMPONENT, CREATE_COMPONENT, CONTEXT_COMPONENT } from "./types/symbols.mjs";
function isEmptyObject(obj) {
  return Object.getOwnPropertyNames(obj).length === 0 && Object.getOwnPropertySymbols &&
  // For IE 11
  Object.getOwnPropertySymbols(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;
}
function createGetField(enhancements, Component, isFunction) {
  return function getField(key) {
    return enhancements.filter(function (enhancement) {
      if (!enhancement.condition) {
        return true;
      }
      return enhancement.condition(Component, isFunction);
    }).reduce(function (acc, item) {
      if (item[key]) {
        acc.push(item[key]);
      }
      return acc;
    }, []);
  };
}
function createForwardWrapper(Component, wrapperProps, statics, isFunction) {
  var RootComponent = Component[ROOT_COMPONENT];
  var getterMethodName = getterMethodNameByDisplayName(Component === null || Component === void 0 ? void 0 : Component.displayName);
  var getterMethod = RootComponent !== null && RootComponent !== void 0 && RootComponent.prototype ? RootComponent.prototype[getterMethodName] : undefined;
  var useGetterIndex = (getterMethod === null || getterMethod === void 0 ? void 0 : getterMethod.length) >= 2;
  function WrapperForwardRefWithBind(_ref, ref) {
    var _ref$forwardRef = _ref.forwardRef,
      forwardRef = _ref$forwardRef === void 0 ? null : _ref$forwardRef,
      other = _objectWithoutProperties(_ref, _excluded);
    return /*#__PURE__*/React.createElement(BindingWrapper, _extends({}, other, {
      forwardRef: useForkRef(ref, forwardRef)
    }));
  }
  function WrapperForwardRef(_ref2, ref) {
    var _ref2$forwardRef = _ref2.forwardRef,
      forwardRef = _ref2$forwardRef === void 0 ? null : _ref2$forwardRef,
      __WRAPPER_PROPS_BIND__ = _ref2.__WRAPPER_PROPS_BIND__,
      other = _objectWithoutProperties(_ref2, _excluded2);
    var _reduce = (__WRAPPER_PROPS_BIND__ || wrapperProps).reduce(function (acc, enhancement) {
        return enhancement(acc, WrapperComponent, isFunction);
      }, other),
      _reduce$ref = _reduce.ref,
      enhancementRef = _reduce$ref === void 0 ? null : _reduce$ref,
      props = _objectWithoutProperties(_reduce, _excluded3);
    return /*#__PURE__*/React.createElement(Component, _extends({}, props, {
      forwardRef: useForkRef(enhancementRef, ref, forwardRef)
    }));
  }
  var BindingWrapper = /*#__PURE__*/function (_React$Component) {
    _inherits(BindingWrapper, _React$Component);
    var _super = _createSuper(BindingWrapper);
    function BindingWrapper() {
      var _this;
      _classCallCheck(this, BindingWrapper);
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      _this = _super.call.apply(_super, [this].concat(args));
      _defineProperty(_assertThisInitialized(_this), "index", -1);
      return _this;
    }
    _createClass(BindingWrapper, [{
      key: "render",
      value: function render() {
        var _this2 = this;
        return /*#__PURE__*/React.createElement(WrapperForwardRef, _extends({}, this.props, {
          __WRAPPER_PROPS_BIND__: wrapperProps.map(function (fn) {
            return fn.bind(_this2);
          })
        }));
      }
    }]);
    return BindingWrapper;
  }(React.Component);
  var WrapperComponent = /*#__PURE__*/React.forwardRef(useGetterIndex ? WrapperForwardRefWithBind : WrapperForwardRef);
  hoistNonReactStatics(WrapperComponent, Component);
  WrapperComponent.displayName = Component.displayName;
  // TODO: defaultProps is only empty
  WrapperComponent.defaultProps = Component.defaultProps;
  // STATIC
  statics.forEach(function (enhancement) {
    return Object.assign(WrapperComponent, enhancement(WrapperComponent, Component, isFunction));
  });
  return WrapperComponent;
}
function wrapClass(OriginComponent, enhancements, Context) {
  var getField = createGetField(enhancements, OriginComponent, false);
  var inits = getField('init');
  var props = getField('asProps');
  var renders = getField('render');
  var statics = getField('static');
  var wrapperProps = getField('wrapperProps');
  var contexts = getField('context');
  var Component = /*#__PURE__*/function (_OriginComponent2) {
    _inherits(Component, _OriginComponent2);
    var _super2 = _createSuper(Component);
    function Component(props, context) {
      var _this3;
      _classCallCheck(this, Component);
      _this3 = _super2.call(this, props, context);
      // INITS
      _defineProperty(_assertThisInitialized(_this3), CORE_AS_PROPS, null);
      _defineProperty(_assertThisInitialized(_this3), CORE_INSTANCE, WrapperComponent);
      _defineProperty(_assertThisInitialized(_this3), CORE_INIT, false);
      inits.forEach(function (enhancement) {
        return enhancement.call(_assertThisInitialized(_this3), props, WrapperComponent, false);
      });
      _this3[CORE_INIT] = true;
      return _this3;
    }
    _createClass(Component, [{
      key: "asProps",
      get: function get() {
        var _this4 = this;
        // TODO if not production
        if (!this[CORE_INIT]) {
          throw new Error('Can not call asProps in constructor');
        }
        if (!this[CORE_AS_PROPS]) {
          // PROPS
          this[CORE_AS_PROPS] = props.reduce(function (acc, enhancement) {
            return enhancement.call(_this4, acc, WrapperComponent, false);
          }, this.props);
        }
        return this[CORE_AS_PROPS];
      }
    }, {
      key: "setContext",
      value: function setContext() {
        var _this5 = this;
        var contextProps = _get(_getPrototypeOf(Component.prototype), "setContext", this) ? _get(_getPrototypeOf(Component.prototype), "setContext", this).call(this) : {};
        // CONTEXT
        return contexts.reduce(function (acc, enhancement) {
          return enhancement.call(_this5, acc, WrapperComponent, false);
        }, contextProps);
      }
    }, {
      key: "render",
      value: function render() {
        var _this6 = this;
        this[CORE_AS_PROPS] = null;
        // TODO if not production
        if (!_get(_getPrototypeOf(Component.prototype), "render", this)) {
          throw new Error('Component `render` method is not defined');
        }
        var asProps = this.asProps;
        var ctx = this.setContext();
        // RENDER
        var render = renders.reduce(function (acc, enhancement) {
          return enhancement.call(_this6, acc, asProps, WrapperComponent, false);
        }, _get(_getPrototypeOf(Component.prototype), "render", this).call(this));
        if (!WrapperComponent[STATIC_COMPONENT] && !isEmptyObject(ctx)) {
          return /*#__PURE__*/React.createElement(Context.Provider, {
            value: _objectSpread(_objectSpread({}, asProps), ctx)
          }, render);
        } else {
          return render;
        }
      }
    }]);
    return Component;
  }(OriginComponent);
  var WrapperComponent = createForwardWrapper(Component, wrapperProps, statics, false);
  return WrapperComponent;
}
function wrapFunction(OriginComponent, enhancements, Context) {
  var getField = createGetField(enhancements, OriginComponent, true);
  var inits = getField('init');
  var props = getField('asProps');
  var renders = getField('render');
  var statics = getField('static');
  var wrapperProps = getField('wrapperProps');
  var contexts = getField('context');
  var Component = /*#__PURE__*/React.memo(function FunctionMemoComponent(other) {
    var selfRef = React.useRef({});
    var firstRender = React.useRef(true);
    useEnhancedEffect(function () {
      firstRender.current = false;
    }, []);
    if (firstRender.current) {
      // INITS
      inits.forEach(function (enhancement) {
        return enhancement.call(selfRef.current, other, WrapperComponent, true);
      });
    }
    // PROPS
    var asProps = props.reduce(function (acc, enhancement) {
      return enhancement.call(selfRef.current, acc, WrapperComponent, true);
    }, other);
    // CONTEXT
    var ctx = contexts.reduce(function (acc, enhancement) {
      return enhancement.call(selfRef.current, acc, WrapperComponent, true);
    }, {});

    // RENDER
    var render = renders.reduce(function (acc, enhancement) {
      return enhancement.call(selfRef.current, acc, asProps, WrapperComponent, true);
    }, /*#__PURE__*/React.createElement(OriginComponent, asProps));
    if (!WrapperComponent[STATIC_COMPONENT] && !isEmptyObject(ctx)) {
      return /*#__PURE__*/React.createElement(Context.Provider, {
        value: _objectSpread(_objectSpread({}, asProps), ctx)
      }, render);
    } else {
      return render;
    }
  });
  Object.assign(Component, OriginComponent);
  var WrapperComponent = createForwardWrapper(Component, wrapperProps, statics, true);
  return WrapperComponent;
}
function wrapCore(OriginComponent, enhancements, Context) {
  var Component = function Component(_ref3) {
    var Root = _ref3.Root;
    var defaultProps = OriginComponent.defaultProps || {};
    return /*#__PURE__*/React.createElement(Root, _extends({}, defaultProps, {
      render: OriginComponent
    }));
  };
  hoistNonReactStatics(Component, OriginComponent);
  Component.displayName = OriginComponent.displayName;
  return wrapFunction(Component, enhancements, Context);
}
function createComposeComponent(OriginComponent, Context, enhancements) {
  if (React.PureComponent.isPrototypeOf(OriginComponent) || React.Component.isPrototypeOf(OriginComponent)) {
    if (OriginComponent.prototype instanceof Component) {
      return wrapClass(OriginComponent, enhancements, Context);
    } else {
      throw new Error('Must inherit from our component');
    }
  } else if (typeof OriginComponent === 'function') {
    return wrapFunction(OriginComponent, enhancements, Context);
  } else if (OriginComponent[CORE_COMPONENT]) {
    return wrapCore(OriginComponent, enhancements, Context);
  } else {
    throw new Error('Must be a React component');
  }
}
function assignProps(p1, p2) {
  return _assignProps(p2, p1);
}
function createComponent(OriginComponent) {
  var childComponents = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _options$context = options.context,
    context = _options$context === void 0 ? /*#__PURE__*/React.createContext({}) : _options$context,
    _options$parent = options.parent,
    parent = _options$parent === void 0 ? [] : _options$parent,
    _options$enhancements = options.enhancements,
    enhancements = _options$enhancements === void 0 ? [] : _options$enhancements;
  var parents = Array.isArray(parent) ? parent : [parent];
  if (parents.length) {
    var wholeFamily = parents.reduce(function (acc, parent) {
      if (parent[PARENT_COMPONENTS]) {
        acc = [].concat(_toConsumableArray(parent[PARENT_COMPONENTS]), _toConsumableArray(acc));
      }
      return acc;
    }, parents);
    OriginComponent[PARENT_COMPONENTS] = wholeFamily;
    parents = wholeFamily;
  }
  if (OriginComponent[CORE_COMPONENT]) {
    parents.push(OriginComponent);
  }
  var Component = createComposeComponent(OriginComponent, context, [].concat(_toConsumableArray(enhancements.map(function (f) {
    return f(context, parents, createComponent, childComponents);
  })), [i18nAppLocaleEnhance(), bindHandlerEnhancement(), childrenEnhancement(context, parents),
  // root must be under the children
  rootEnhancement(), uncontrolledPropsEnhancement(), staticChildrenEnhancement(childComponents, createComponent, {
    context: context,
    parent: parent,
    enhancements: enhancements
  }),
  // functionDefaultProps must be under the staticChild
  functionDefaultPropsEnhancement(),
  // inheritedName must be under the staticChild
  inheritedNameEnhancement(),
  // dataName must be under the staticChild
  dataNameEnhancement(),
  // enhanceEnhancement must be under the functionDefaultPropsEnhancement
  enhanceEnhancement(), styleEnhancement(childComponents, context),
  // must be the last one so any properties can be raised
  hoistPropsEnhancement(childComponents, context)]));
  Component[CONTEXT_COMPONENT] = context;
  Component._______childrenComponents = childComponents;
  Component[CREATE_COMPONENT] = function () {
    var _OriginComponent = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : OriginComponent;
    var _childComponents = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : childComponents;
    var _options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : options;
    return createComponent(_OriginComponent, _childComponents, _options);
  };
  Component[CORE_COMPONENT] = true;
  return Component;
}
function createBaseComponent(OriginComponent) {
  var Component = null;
  if (!React.PureComponent.isPrototypeOf(OriginComponent) && !React.Component.isPrototypeOf(OriginComponent) && typeof OriginComponent === 'function') {
    Component = /*#__PURE__*/React.forwardRef(OriginComponent);
    Component.displayName = OriginComponent.displayName;
    Component.defaultProps = _objectSpread({
      'data-ui-name': OriginComponent.displayName
    }, OriginComponent.defaultProps);
    Component[CORE_COMPONENT] = true;
  } else {
    throw new Error('createBaseComponent accepts only functional component');
  }
  return Component;
}
export * from "./types/index.mjs";
export * from "./styled/index.mjs";
export * from "./register.mjs";
export { createBaseComponent, register, CREATE_COMPONENT, CORE_INSTANCE, CONTEXT_COMPONENT, CHILDREN_COMPONENT, INHERITED_NAME, CORE_COMPONENT, STATIC_COMPONENT, ROOT_COMPONENT, assignProps };
export default createComponent;
//# sourceMappingURL=index.mjs.map